import React from 'react';
import { useJobApplications } from '../../context/JobApplicationContext';
import { format } from 'date-fns';
import { 
  HiOutlinePencil, 
  HiOutlineTrash, 
  HiOutlineArrowTopRightOnSquare,
  HiOutlineMapPin,
  HiOutlineCalendarDays,
  HiOutlineExclamationTriangle
} from 'react-icons/hi2';
import styles from './JobApplicationTable.module.css';

const JobApplicationTable = ({ applications, loading, onEdit }) => {
  const { deleteApplication } = useJobApplications();

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await deleteApplication(id);
      } catch (error) {
        console.error('Failed to delete application:', error);
      }
    }
  };

  const getStatusClass = (status) => {
    return `badge status-badge status-${status.toLowerCase()}`;
  };

  const getPriorityClass = (priority) => {
    return `badge priority-badge priority-${priority.toLowerCase()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const isOverdue = (followUpDate) => {
    if (!followUpDate) return false;
    return new Date(followUpDate) < new Date();
  };

  const isToday = (followUpDate) => {
    if (!followUpDate) return false;
    const today = new Date().toDateString();
    return new Date(followUpDate).toDateString() === today;
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading applications...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className={styles.empty}>
        <HiOutlineExclamationTriangle className={styles.emptyIcon} />
        <h3>No applications found</h3>
        <p>Start by adding your first job application to track your progress.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Company & Role</th>
              <th>Platform</th>
              <th>Applied</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Follow-up</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app) => (
              <tr 
                key={app.id} 
                className={`
                  ${isOverdue(app.follow_up_date) ? styles.overdue : ''}
                  ${isToday(app.follow_up_date) ? styles.today : ''}
                `}
              >
                <td>
                  <div className={styles.companyCell}>
                    <div className={styles.companyInfo}>
                      <strong className={styles.companyName}>{app.company_name}</strong>
                      <div className={styles.roleInfo}>
                        {app.job_url ? (
                          <a 
                            href={app.job_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={styles.jobLink}
                          >
                            {app.role}
                            <HiOutlineArrowTopRightOnSquare className={styles.externalIcon} />
                          </a>
                        ) : (
                          <span className={styles.roleName}>{app.role}</span>
                        )}
                      </div>
                    </div>
                    {app.location && (
                      <div className={styles.location}>
                        <HiOutlineMapPin className={styles.locationIcon} />
                        <span>{app.location}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <span className={styles.platform}>{app.platform}</span>
                </td>
                <td>
                  <div className={styles.dateCell}>
                    <HiOutlineCalendarDays className={styles.dateIcon} />
                    <span>{formatDate(app.applied_date)}</span>
                  </div>
                </td>
                <td>
                  <span className={getStatusClass(app.status)}>
                    {app.status}
                  </span>
                </td>
                <td>
                  <span className={getPriorityClass(app.priority)}>
                    {app.priority}
                  </span>
                </td>
                <td>
                  {app.follow_up_date ? (
                    <div className={`
                      ${styles.followUp}
                      ${isOverdue(app.follow_up_date) ? styles.overdueText : ''}
                      ${isToday(app.follow_up_date) ? styles.todayText : ''}
                    `}>
                      <HiOutlineCalendarDays className={styles.followUpIcon} />
                      <span>{formatDate(app.follow_up_date)}</span>
                    </div>
                  ) : (
                    <span className={styles.noFollowUp}>-</span>
                  )}
                </td>
                <td>
                  <div className={styles.actions}>
                    <button
                      onClick={() => onEdit(app)}
                      className="btn btn-sm btn-ghost"
                      title="Edit application"
                    >
                      <HiOutlinePencil />
                    </button>
                    <button
                      onClick={() => handleDelete(app.id)}
                      className="btn btn-sm btn-ghost"
                      title="Delete application"
                    >
                      <HiOutlineTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobApplicationTable;